// send-engine-stamp.ts (#333 slice 6)
//
// Which engine carried a send (native wa-js / raw Baileys core / raw tried then fell back to
// native), stamped onto messagelogs.metadata.sendEngine so a fleet scan can tell raw sends from
// native ones. The page world already records the engine on the send trace
// (`@engine:native|raw|raw-fallback`); the content-side thunk reads it back from the trace and
// stashes it in the per-message send-log holder.
//
// Flags OFF => nothing is read and nothing is stamped (an absent field means native), so the
// default path is byte-identical to before: no extra trace IPC, no new field on any log row.

import {
  RAW_SEND_ENABLED,
  readLocalRawSendOverride,
  type SendEngine,
} from '../message/raw-send/raw-send-gate';

export type { SendEngine };

/**
 * True only when the raw gate COULD have engaged for this message (build flag AND either the
 * per-message server flag or the local override). Mirrors the first three layers of `useRawSend`
 * minus the message-shape guard, which is irrelevant here: the trace says what really happened.
 */
export function shouldStampSendEngine(
  sendEngine: unknown,
  buildFlag: boolean = RAW_SEND_ENABLED,
  localOverride?: boolean,
): boolean {
  if (!buildFlag) return false;
  if (sendEngine === 'raw') return true;
  return localOverride ?? readLocalRawSendOverride();
}

/**
 * Read the engine off a formatted send trace. Events render as `@<label>=<ms>`; the swap point
 * marks `engine:native`, `engine:raw`, `engine:raw-fallback`, and `raw:threw-after-ack` (raw went
 * out and was acked, then threw: still a raw send). Returns undefined when the trace names none
 * (unavailable trace, or the send never reached the swap point). Never throws.
 */
export function parseSendEngineFromTrace(trace: string | undefined | null): SendEngine | undefined {
  const t = trace ?? '';
  if (/(?:^|\s)@engine:raw-fallback(?![\w-])/.test(t)) return 'raw-fallback';
  if (/(?:^|\s)@engine:raw(?![\w-])/.test(t) || /(?:^|\s)@raw:threw-after-ack(?![\w-])/.test(t)) return 'raw';
  if (/(?:^|\s)@engine:native(?![\w-])/.test(t)) return 'native';
  return undefined;
}

/**
 * The whole stamping step, kept out of the thunk so it can be tested: flags OFF => `fetchTraceText`
 * is never called (no IPC) and nothing is stashed. `sendEngine` must be the flag from the SAME
 * doc the page gate reads (the lock-returned message), or a server-set raw flag would send raw
 * and skip the stamp. Best-effort: never throws.
 */
export async function stampSendEngineFromTrace(input: {
  sendEngine: unknown;
  userMessageId: string;
  fetchTraceText: (userMessageId: string) => Promise<string>;
  setMetadata: (userMessageId: string, meta: { sendEngine: SendEngine }) => void;
  buildFlag?: boolean;
  localOverride?: boolean;
}): Promise<SendEngine | undefined> {
  try {
    if (!shouldStampSendEngine(input.sendEngine, input.buildFlag, input.localOverride)) return undefined;
    const engine = parseSendEngineFromTrace(await input.fetchTraceText(input.userMessageId));
    if (engine) input.setMetadata(input.userMessageId, { sendEngine: engine });
    return engine;
  } catch (err) {
    console.warn('[sendEngine] stamping failed (non-fatal):', err);
    return undefined;
  }
}
